/**
 * Requests for the Create User wizard (PD-4064).
 *
 * Sibling of usersService, which serves the list page. Everything talks to
 * Management::UsersController, so the paths are the page's own.
 */

const getJson = async (path) => {
  const response = await fetch(path, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

const csrfToken = () =>
  document.querySelector('[name="csrf-token"]')?.getAttribute('content') ?? '';

export const fetchFormOptions = () =>
  getJson('/management/users/form_options.json');

/**
 * One page of assignable accounts, plus how many match in total.
 *
 * Paged because an OEM admin can administer thousands: the wizard shows a page and
 * asks for the next as the admin scrolls. `total` is what lets it say so — a page
 * on its own cannot report whether it is all of them.
 *
 * @param {string} query    server-side name filter
 * @param {number} offset   how many to skip; 0 is the first page
 * @returns {Promise<{accounts: Array, total: number}>}
 */
export const fetchAdminAccounts = async (query, offset = 0) => {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (offset) params.set('offset', String(offset));

  const suffix = params.toString() ? `?${params}` : '';
  const data = await getJson(`/management/users/admin_accounts.json${suffix}`);

  return {
    accounts: data.accounts || [],
    // Older responses carried no total; fall back to what arrived rather than
    // reporting zero, which would read as "no accounts".
    total:
      typeof data.total === 'number'
        ? data.total
        : (data.accounts || []).length,
  };
};

/**
 * What the server makes of the email and mobile the admin just left.
 *
 * Reports whether each already belongs to someone, and whether the number is well
 * formed — the per-country phone rules are Phonelib's and live on the server, so
 * asking is the only way to agree with what the model will accept.
 *
 * Both halves of the mobile are needed for either answer to mean anything, so a
 * number with no country code chosen yet reports as available and valid.
 */
export const fetchAvailability = async ({
  email,
  mobile,
  mobileCountryCode,
} = {}) => {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (mobile) params.set('mobile', mobile);
  if (mobileCountryCode) params.set('mobile_country_code', mobileCountryCode);

  const data = await getJson(`/management/users/availability.json?${params}`);

  return {
    emailTaken: data.emailTaken,
    mobileTaken: data.mobileTaken,
    mobileValid: data.mobileValid,
  };
};

/**
 * Creates the user.
 *
 * Resolves with the created user on 201. On 422 it throws an error carrying the
 * model's field errors, so the wizard can point at the offending step; anything
 * else throws bare, which lands on the design's generic error panel.
 */
export const createUser = async (payload) => {
  const response = await fetch('/management/users.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    const data = await response.json();
    return data.user;
  }

  const error = new Error(`HTTP error! status: ${response.status}`);

  if (response.status === 422) {
    // A 422 body is ours, but do not let a malformed one mask the failure.
    error.fieldErrors = await response
      .json()
      .then((data) => data.errors ?? {}, () => ({}));
  }

  throw error;
};

export default createUser;
